import React from 'react';
import styled from 'astroturf/react';
import { BodyLink } from 'js/design_system/link/BodyLink';
import { UsherTasks } from 'js/utils/usher/tasks';

type EvaluationResult =
  UsherTasks['citeright_evaluate_publication_finder']['result'];
export type Publication =
  EvaluationResult[number]['ranked_publications'][number];

const Card = styled.div`
  border: 1px solid var(--stroke-light);
  border-radius: var(--border-radius-radius-sm);
  padding: var(--spacing-xs-16);
  margin: var(--spacing-xs-12) 0;
`;

const Title = styled.div`
  font-weight: 600;
  margin-bottom: var(--spacing-xs-8);
`;

const VerificationBadge = styled.div`
  background: var(--brand-colors-blue-100);
  padding: var(--spacing-xs-8) var(--spacing-xs-12);
  border-radius: var(--border-radius-radius-sm);
  margin-top: var(--spacing-xs-8);
`;

const MetaRow = styled.div`
  display: flex;
  gap: var(--spacing-xs-16);
  flex-wrap: wrap;
  margin-top: var(--spacing-xs-8);
  font-size: 13px;
  color: var(--text-secondary);
`;

interface WorkLinkProps {
  workId: number;
}

const WorkLink: React.FC<WorkLinkProps> = ({ workId }) => {
  if (workId < 0) {
    return (
      <BodyLink href={`https://openalex.org/works/w${-workId}`} external>
        Link to work in OpenAlex
      </BodyLink>
    );
  }
  return (
    <BodyLink href={`/works/${workId}`} external>
      Link to work in Academia
    </BodyLink>
  );
};

interface EvaluationPublicationCardProps {
  publication: Publication;
}

export const EvaluationPublicationCard: React.FC<
  EvaluationPublicationCardProps
> = ({ publication }) => {
  return (
    <Card>
      <Title>{publication.title || 'Untitled'}</Title>
      <MetaRow>
        <span>
          <WorkLink workId={publication.work_id} />
        </span>
        {publication.vector_score != null && (
          <span>
            <strong>Vector Score:</strong> {publication.vector_score.toFixed(4)}
          </span>
        )}
      </MetaRow>
      {publication.reasoning && (
        <div style={{ marginTop: '8px' }}>
          <strong>Reasoning:</strong> {publication.reasoning}
        </div>
      )}
      {(publication.verification_category ||
        publication.verification_reasoning) && (
        <VerificationBadge>
          {publication.verification_category && (
            <div>
              <strong>Verification:</strong> {publication.verification_category}
            </div>
          )}
          {publication.verification_reasoning && (
            <div style={{ marginTop: '4px' }}>
              {publication.verification_reasoning}
            </div>
          )}
        </VerificationBadge>
      )}
      {publication.abstract && (
        <div style={{ marginTop: '8px' }}>
          <summary style={{ cursor: 'pointer', fontSize: '13px' }}>
            Abstract
          </summary>
          <p
            style={{
              fontSize: '13px',
              marginTop: '8px',
              whiteSpace: 'pre-wrap',
            }}
          >
            {publication.abstract}
          </p>
        </div>
      )}
    </Card>
  );
};

export default EvaluationPublicationCard;
